import { appAccessEvaluator } from '@electron/shared/app-access-policy';

import { electronApis } from '@/features/electron-api';
import { logError } from '@/lib/errorLogging';
import { userStore } from '@/stores/user.store';
import { inElectron } from '@/utils';

import { copyAppContextForDebug } from './app-context-capture';
import { type MentionItem, type MentionSource } from './mention';

import type { AppAccessPolicy } from '@electron/shared/types';

const APP_KIND = 'app';

const EXCLUDED_SECTION_TITLE = 'attach an excluded app';

export type MentionedApp = {
  pid: number;
  windowId: number | null;
  name: string;
  iconUrl: string | null;
  bundleId: string | null;
  windowTitle: string | null;
};

export const appToMention = (
  app: MentionedApp,
  { autoIncluded = false }: { autoIncluded?: boolean } = {}
): MentionItem => ({
  id: app.windowId === null ? `process:${app.pid}` : `window:${app.windowId}`,
  kind: APP_KIND,
  name: app.name,
  iconUrl: app.iconUrl,
  detail:
    app.windowTitle && app.windowTitle !== app.name ? app.windowTitle : null,
  data: {
    pid: app.pid,
    windowId: app.windowId,
    bundleId: app.bundleId,
    windowTitle: app.windowTitle,
  },
  autoIncluded,
});

export const mentionToApp = (mention: MentionItem): MentionedApp | null => {
  if (mention.kind !== APP_KIND) return null;
  const { pid, windowId, bundleId, windowTitle } = mention.data ?? {};
  if (typeof pid !== 'number') return null;
  return {
    pid,
    windowId: typeof windowId === 'number' ? windowId : null,
    name: mention.name,
    iconUrl: mention.iconUrl ?? null,
    bundleId: typeof bundleId === 'string' ? bundleId : null,
    windowTitle: typeof windowTitle === 'string' ? windowTitle : null,
  };
};

export const mentionedApps = (mentions: MentionItem[]): MentionedApp[] =>
  mentions.flatMap((mention) => {
    const app = mentionToApp(mention);
    return app ? [app] : [];
  });

export const createAppSource = ({
  listApps,
  onSelect,
}: {
  listApps: () => Promise<MentionedApp[]>;
  onSelect?: (app: MentionedApp) => void;
}): MentionSource => ({
  kind: APP_KIND,
  title: 'Attach an app',
  listItems: async () => {
    let policy: AppAccessPolicy | undefined;
    try {
      policy = await electronApis.appExclusion.getAppAccessPolicy();
    } catch (error) {
      logError(error, 'App mentions hidden: app access policy unavailable');
      return [];
    }
    if (!policy && inElectron()) return [];

    const apps = await listApps();
    const isExcludedByPolicy = policy
      ? appAccessEvaluator(policy)
      : () => false;
    const isExcluded = (app: MentionedApp) => isExcludedByPolicy(app.bundleId);

    return [
      ...apps.filter((app) => !isExcluded(app)).map((app) => appToMention(app)),
      ...apps.filter(isExcluded).map((app) => ({
        ...appToMention(app),
        section: EXCLUDED_SECTION_TITLE,
      })),
    ];
  },
  onSelect: (mention) => {
    const app = mentionToApp(mention);
    if (app) onSelect?.(app);
  },
  onContextMenu: (mention, event) => {
    if (!userStore.isInternalUser) return;
    const app = mentionToApp(mention);
    if (!app) return;
    event.preventDefault();
    void copyAppContextForDebug(app);
  },
});
